/**
 * User action logger for tracking client-server interactions
 *
 * Provides consistent logging for user actions and server responses.
 * All user-initiated actions should use this logger to show:
 * 1. When an action is attempted
 * 2. Whether it succeeded or failed (with server message)
 */

type ActionStatus = 'attempt' | 'success' | 'failure';

interface LogOptions {
  details?: Record<string, unknown>;
}

function formatDetails(details?: Record<string, unknown>): string {
  if (!details || Object.keys(details).length === 0) return '';
  const pairs = Object.entries(details).map(([k, v]) => `${k}=${JSON.stringify(v)}`);
  return ` (${pairs.join(', ')})`;
}

function log(action: string, status: ActionStatus, message?: string, options?: LogOptions): void {
  const prefix = status === 'attempt' ? '→' : status === 'success' ? '✓' : '✗';
  const details = formatDetails(options?.details);
  const msg = message ? `: ${message}` : '';
  // console.log(`[ACTION] ${prefix} ${action}${msg}${details}`);
}

export const ActionLogger = {
  /**
   * Log when an action is being attempted
   */
  attempt(action: string, options?: LogOptions): void {
    log(action, 'attempt', undefined, options);
  },

  /**
   * Log when an action succeeded
   */
  success(action: string, message?: string, options?: LogOptions): void {
    log(action, 'success', message, options);
  },

  /**
   * Log when an action failed with server message
   */
  failure(action: string, error: unknown, options?: LogOptions): void {
    const message = error instanceof Error ? error.message : String(error);
    const details = formatDetails(options?.details);
    const msg = message ? `: ${message}` : '';
    console.error(`[ACTION] ✗ ${action}${msg}${details}`);
  },

  /**
   * Wrap an async action with attempt/success/failure logging
   */
  async wrap<T>(action: string, fn: () => Promise<T>, options?: LogOptions): Promise<T> {
    this.attempt(action, options);
    try {
      const result = await fn();
      this.success(action, undefined, options);
      return result;
    } catch (error) {
      this.failure(action, error, options);
      throw error;
    }
  }
};
